import React from "react";
import { useNavigate } from "react-router-dom";

import WappIcono from "../../../components/wappIcono";
import { formatPhoneDisplay } from "../../../components/phoneField";
import { partirEmojisPropios } from "./equipoGifs";

/**
 * Texto de un mensaje de WappTeam: ligas, @menciones y clientes.
 *
 * El back cambia las ligas a la ficha de un cliente (Zoho CRM, ya apagado, y /prospects?id= de WAPP) por un marcador [[cliente:<id>|<nombre>|<teléfono>]]
 * (EnlacesCliente.php); aquí se pinta como el nombre y teléfono del cliente, con enlace a su ficha en el CRM de WAPP.
 * Con `cortas`, las ligas largas se muestran con un nombre corto (la sección de WAPP o el dominio) en vez de la URL.
 */
const PARTES_RE = /(\[\[cliente:\d+\|[^|\]\n]*\|[^|\]\n]*\]\]|https?:\/\/[^\s<]+[^\s<.,;:!?)\]'"])/g;
const CLIENTE_RE = /^\[\[cliente:(\d+)\|([^|\]\n]*)\|([^|\]\n]*)\]\]$/;
const MENCION_RE = /(@[A-Za-zÁÉÍÓÚÑáéíóúñü]+)/;
/** Un emoji completo (con tono de piel, variación y uniones ZWJ), para darle tamaño propio en los avisos. */
const EMOJI_RE = /(\p{Extended_Pictographic}[\u{FE0F}\u{1F3FB}-\u{1F3FF}]*(?:\u200D\p{Extended_Pictographic}[\u{FE0F}\u{1F3FB}-\u{1F3FF}]*)*)/u;

/** Secciones de WAPP que aparecen en los avisos (paymentIntents?ticket=762 → "Pagos por Procesar #762"). */
const SECCIONES: { [ruta: string]: string } = {
	paymentIntents: "Pagos por Procesar", enrollmentRequests: "Matrícula", prospects: "CRM", wappchat: "WappChat", wappteam: "WappTeam",
	students: "Alumnos", payments: "Pagos", preSolicitudes: "Solicitudes", wappdrive: "WappDrive",
};
/** Siempre con lada (+504 8792 6080): los avisos mezclan números de Honduras y México. "0" = ningún país por defecto. */
const telConLada = (t: string): string => formatPhoneDisplay(t, "0") || t;

//wapp.washingtonacademy.com desde el 21-oct-2026; las ligas viejas de app. siguen siendo de WAPP.
const DOMINIOS_WAPP = /^((app|nuevo)\.washingtonacademy\.mx|wapp\.washingtonacademy\.com)$/i;

const ligaCorta = (url: string): { texto: string; interna: string | null } => {
	try {
		const u = new URL(url);
		if (DOMINIOS_WAPP.test(u.hostname) || u.host === window.location.host) {
			const ruta = u.pathname.replace(/^\/+/, "").split("/")[0];
			const num = u.searchParams.get("ticket") || u.searchParams.get("solicitud");
			const nombre = SECCIONES[ruta] || "Abrir en WAPP";
			return { texto: nombre + (num ? " #" + num : ""), interna: u.pathname + u.search };
		}
		return { texto: u.hostname.replace(/^www\./, ""), interna: null };
	} catch (e) {
		return { texto: url, interna: null };
	}
};

/** Clic normal: navega dentro de WAPP. Con Cmd/Ctrl o botón central: deja que el navegador abra otra pestaña. */
const clicInterno = (navigate: (to: string) => void, ruta: string) => (e: React.MouseEvent) => {
	if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
	e.preventDefault();
	e.stopPropagation();
	navigate(ruta);
};

/** cliq: los emojis propios de Cliq (:carla:) se ven como etiqueta (WappTeam). */
const TextoRico: React.FC<{ t: string; cortas?: boolean; className?: string; cliq?: boolean }> = ({ t, cortas, className, cliq }) => {
	const navigate = useNavigate();
	const partes = t.split(PARTES_RE);
	return (
		<p className={className}>
			{partes.map((p, i) => {
				if (i % 2 === 0) {
					return <React.Fragment key={i}>{p.split(MENCION_RE).map((x, j) => (j % 2 === 1 ? <b key={j} className="eq-mencion">{x}</b>
						: cortas ? x.split(EMOJI_RE).map((y, k) => (k % 2 === 1 ? <span key={k} className="tr-emoji">{y}</span> : y)) : cliq ? <React.Fragment key={j}>{partirEmojisPropios(x)}</React.Fragment> : x))}</React.Fragment>;
				}
				const c = CLIENTE_RE.exec(p);
				if (c) {
					const ruta = "/prospects?id=" + c[1];
					const tel = c[3] ? telConLada(c[3]) : "";
					return (
						<a key={i} className="tr-cliente" href={ruta} onClick={clicInterno(navigate, ruta)} title="Abrir en el CRM">
							<WappIcono n="user" />
							{c[2] ? <b>{c[2]}</b> : null}
							{c[2] && tel ? <span className="sep" aria-hidden="true">·</span> : null}
							{tel ? <span className="tel">{tel}</span> : null}
							{!c[2] && !tel ? <b>Cliente</b> : null}
						</a>
					);
				}
				if (!cortas) return <a key={i} href={p} target="_blank" rel="noopener noreferrer">{p}</a>;
				const l = ligaCorta(p);
				return l.interna
					? <a key={i} className="tr-liga" href={l.interna} onClick={clicInterno(navigate, l.interna)} title={p}>{l.texto}</a>
					: <a key={i} className="tr-liga" href={p} target="_blank" rel="noopener noreferrer" title={p}>{l.texto}<WappIcono n="open" /></a>;
			})}
		</p>
	);
};

export default TextoRico;
